import React from 'react';
import { PropTypes, describe } from '@elliemae/ds-props-helpers';
import { times } from 'lodash-es';
import { truncate, styled } from '@elliemae/ds-system';
import { Grid } from '@elliemae/ds-grid';

const Title = styled.h3`
  font-size: 14px;
  margin: 0;
  ${truncate()}
  color: ${(props) => props.theme.colors.neutral['700']};
  line-height: inherit;
  text-transform: capitalize;
  font-weight: ${(props) => props.theme.fontWeights.semibold};
`;

const GroupBox = styled(Grid)`
  background: ${(props) =>
    props.background === 'grey' ? props.theme.colors.neutral['050'] : props.theme.colors.neutral['000']};

  border-top: ${(props) => (props.borderTop ? `1px solid ${props.theme.colors.neutral['100']}` : 'none')};
`;

const DSGroupBox = ({ children, title, background = 'grey', headerLevel = 3, actionsRight = [], borderTop }) => {
  const rows = ['auto'];
  const hasActions = actionsRight.length > 0;
  if (hasActions) rows.push('auto');
  return (
    <Grid rows={['32px', 'auto']}>
      <GroupBox
        background={background}
        cols={rows}
        borderTop={borderTop}
        alignItems="center"
        pl="xs"
        pr={hasActions ? 'xxs' : 'xs'}
      >
        <Title as={`h${headerLevel}`}>{title}</Title>
        {hasActions && (
          <Grid cols={times(actionsRight.length, () => 'auto')} justifySelf="end">
            {actionsRight}
          </Grid>
        )}
      </GroupBox>
      {children}
    </Grid>
  );
};

const props = {
  /**
   * In-box children
   */
  children: PropTypes.element.description('In-box children'),
  /**
   * Title background
   */
  background: PropTypes.oneOf(['white', 'grey']).description('Title background'),
  /**
   * Toggle border top of group box
   */
  borderTop: PropTypes.bool.description('Toggle border top of group box'),
  /**
   * Group Box title
   */
  title: PropTypes.string.isRequired.description('Group Box title'),
  /**
   * semantic level of header tag for the title
   */
  headerLevel: PropTypes.oneOf([1, 2, 3, 4, 5, 6]).description('semantic level of header tag for the title'),
  /**
   * Array of button icons
   */
  actionsRight: PropTypes.arrayOf(PropTypes.element).description('Array of button icons'),
};

DSGroupBox.displayName = 'DSGroupBox';
const DSGroupBoxWithSchema = describe(DSGroupBox);
DSGroupBoxWithSchema.propTypes = props;

export { DSGroupBox, DSGroupBoxWithSchema };
